import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownRight,
  Calendar,
  Filter,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import { Transaction, WalletTransactionType, Wallet as WalletType } from "./types";
import { useModal } from "../../hooks/useModal";
import { fetchWallets, ListTransactions } from "./API/services";
import Spinner from "../ui/Spinner/Spinner";
import PageMeta from "../common/PageMeta";
import PageBreadcrumb from "../common/PageBreadCrumb";
import TransactionSummary from "./modals/TransactionSummary";

interface ApiResponse {
  transactions: Transaction[];
  limit: number;
  pageCount: number;
  pageNo: number;
  totalCount: number;
}

interface Props {
  wallet?: WalletTransactionType | null;
}

const TransactionsPage = ({ wallet: walletProp }: Props) => {
  const navigate = useNavigate();
  const { currency } = useParams<{ currency?: string }>();

  const [wallet, setWallet] = useState<WalletType | null>(walletProp ?? null);
  const [walletLoading, setWalletLoading] = useState(!walletProp);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [typeFilter, setTypeFilter] = useState<string>("");
const [statusFilter, setStatusFilter] = useState<string>("");
  const limit = 20;

  const { isOpen: isSummaryOpen, openModal: openSummaryModal, closeModal: closeSummaryModal } =
    useModal();

  useEffect(() => {
    if (!walletProp && currency) {
      setWalletLoading(true);
      fetchWallets()
        .then((wallets) => {
          const found = (wallets as WalletType[]).find(
            (w) => w.currency === currency.toUpperCase()
          );
          setWallet(found ?? null);
        })
        .finally(() => setWalletLoading(false));
    }
  }, [walletProp, currency]);

  useEffect(() => {
  if (wallet) fetchTransactions();
}, [wallet, pageNo, typeFilter, statusFilter]);

const handleTypeChange = (value: string) => {
  setTypeFilter(value);
  setPageNo(1);
};

const handleStatusChange = (value: string) => {
  setStatusFilter(value);
  setPageNo(1);
};

  const fetchTransactions = async () => {
  if (!wallet) return;
  try {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      currency: wallet.currency,
      pageNo: String(pageNo),
      limit: String(limit),
      orderBy: "date",
      order: "desc",
    });

    if (typeFilter) params.append("type", typeFilter);
    if (statusFilter) params.append("status", statusFilter);

    const response = (await ListTransactions(params.toString())) as ApiResponse;
    setTransactions(response?.transactions || []);
    setPageCount(response?.pageCount ?? 1);
    setTotalCount(response?.totalCount ?? 0);
  } catch (err: any) {
    setError(err?.response?.data?.message || "Failed to fetch transactions");
    setTransactions([]);
  } finally {
    setLoading(false);
  }
};

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getStatusBadgeColor = (status: string): "success" | "warning" | "error" | "info" => {
    switch (status) {
      case "SUCCESSFUL": return "success";
      case "PENDING":    return "warning";
      case "FAILED":     return "error";
      default:           return "info";
    }
  };

  const getDisplayStatus = (status: string) =>
    status === "UNKNOWN" ? "CANCELLED" : status;

  const getAmountStyle = (transaction: Transaction) => {
    if (!wallet) return { sign: "", colorClass: "", symbol: "" };
    const symbol = transaction.symbol || wallet.symbol;
    if (transaction.status === "SUCCESSFUL") {
      if (transaction.type === "CREDIT" || transaction.type === "RELEASE")
        return { sign: "+", colorClass: "text-green-600 dark:text-green-400", symbol };
      if (transaction.type === "DEBIT" || transaction.type === "HOLD")
        return { sign: "-", colorClass: "text-red-600 dark:text-red-400", symbol };
    }
    if (transaction.status === "PENDING")
      return { sign: "", colorClass: "text-yellow-600 dark:text-yellow-400", symbol };
    return { sign: "", colorClass: "text-gray-600 dark:text-gray-400", symbol };
  };

  if (walletLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="h-10 w-10 text-primary" />
      </div>
    );
  }

  if (!wallet) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4 text-center p-6">
        <p className="text-gray-600 dark:text-gray-400">Wallet not found.</p>
        <button
          onClick={() => navigate(-1)}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
        >
          Go Back
        </button>
      </div>
    );
  }

  const startEntry = totalCount === 0 ? 0 : (pageNo - 1) * limit + 1;
  const endEntry = Math.min(pageNo * limit, totalCount);

  return (
    <>
      <PageMeta
        title={`${wallet.name} Transactions`}
        description={`View all transactions for your ${wallet.currency} wallet`}
      />
      <PageBreadcrumb pageTitle={`${wallet.name} Transactions`} />

      <div className="p-4 sm:p-6 max-w-6xl mx-auto">

        {/* Page header */}
        <div className="mb-6">
          <div className="mb-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400"
              aria-label="Go back"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            {wallet.flag && (
              <img src={wallet.flag} alt={wallet.currency} className="h-6 w-6 rounded-full" />
            )}
            <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
              {wallet.name} Wallet Transactions
            </h1>
          </div>

          {/* <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {wallet.symbol} {wallet.balance.toLocaleString()}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="h-4 w-4" />
              {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </div>
          </div> */}
        </div>

        {/* Table card */}
        <div className="overflow-hidden w-full rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
          {/* Card header */}
<div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
  <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
    All Transactions
  </h3>

  <div className="flex items-center gap-2">
    <select
      value={typeFilter}
      onChange={(e) => handleTypeChange(e.target.value)}
      className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-theme-sm text-gray-700 shadow-theme-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
    >
      <option value="">All Types</option>
      <option value="CREDIT">Credit</option>
      <option value="DEBIT">Debit</option>
    </select>

    <select
      value={statusFilter}
      onChange={(e) => handleStatusChange(e.target.value)}
      className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-theme-sm text-gray-700 shadow-theme-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
    >
      <option value="">All Statuses</option>
      <option value="SUCCESSFUL">Successful</option>
      <option value="PENDING">Pending</option>
      <option value="FAILED">Failed</option>
      <option value="CANCELLED">Cancelled</option>
    </select>

    <button
      onClick={fetchTransactions}
      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
    >
      <RefreshCcw className="h-4 w-4" />
      <span className="hidden sm:inline">Refresh</span>
    </button>
  </div>
</div>

          {/* Scrollable table — fixed height, only this scrolls */}
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-360px)] min-h-[200px]">
            {loading ? (
              <div className="flex h-48 items-center justify-center">
                <Spinner className="h-10 w-10 text-primary" />
              </div>
            ) : error ? (
              <div className="flex h-48 flex-col items-center justify-center gap-4 text-center p-6">
                <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                  <ArrowDownRight className="h-6 w-6 text-red-500" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">{error}</p>
                <button
                  onClick={fetchTransactions}
                  className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
                >
                  Retry
                </button>
              </div>
            ) : transactions.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center gap-3 text-center p-6">
                <div className="rounded-full bg-gray-100 p-3 dark:bg-gray-800">
                  <Filter className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">No transactions found</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  After your first transaction, you will be able to view it here
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader className="border-gray-100 dark:border-gray-800 border-y sticky top-0 bg-white dark:bg-gray-900 z-10">
                  <TableRow>
                    <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                      Description
                    </TableCell>
                    <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                      Type
                    </TableCell>
                    <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                      Amount
                    </TableCell>
                    <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                      Date
                    </TableCell>
                    <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                      Status
                    </TableCell>
                    <TableCell isHeader className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHeader>

                <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {transactions.map((transaction) => {
                    const { sign, colorClass, symbol } = getAmountStyle(transaction);
                    return (
                      <TableRow key={transaction.id}>
                        {/* Description */}
                        <TableCell className="py-3 min-w-48 text-gray-800 text-theme-sm dark:text-white/90">
                          <div className="flex items-center gap-2">
                            {(transaction.flag || wallet.flag) && (
                              <img
                                src={transaction.flag || wallet.flag}
                                className="h-6 w-6 rounded-full flex-shrink-0"
                                alt={transaction.currency}
                              />
                            )}
                            <div>
                              <p className="font-medium truncate max-w-48">
                                {transaction.purpose || transaction.narration || "—"}
                              </p>
                              {transaction.narration &&
                                transaction.purpose &&
                                transaction.narration !== transaction.purpose && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-48">
                                    {transaction.narration}
                                  </p>
                                )}
                            </div>
                          </div>
                        </TableCell>

                        {/* Type */}
                        <TableCell className="py-3 min-w-24 text-gray-500 text-theme-sm dark:text-gray-400">
                          {transaction.type}
                        </TableCell>

                        {/* Amount */}
                        <TableCell className={`py-3 min-w-32 text-theme-sm font-semibold ${colorClass}`}>
                          {sign} {symbol} {Math.abs(transaction.amount).toLocaleString()}
                          {transaction.fee > 0 && (
                            <p className="text-xs font-normal text-gray-400 dark:text-gray-500">
                              Fee: {symbol} {transaction.fee}
                            </p>
                          )}
                        </TableCell>

                        {/* Date */}
                        <TableCell className="py-3 min-w-40 text-gray-500 text-theme-sm dark:text-gray-400">
                          {formatDate(transaction.createdAt)}
                        </TableCell>

                        {/* Status */}
                        <TableCell className="py-3 pr-4">
                          <Badge size="sm" color={getStatusBadgeColor(transaction.status)}>
                            {getDisplayStatus(transaction.status)}
                          </Badge>
                        </TableCell>

                        {/* Action */}
                        <TableCell>
                          <button
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              openSummaryModal();
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-1 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
                          >
                            View
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Footer: entry count + pagination */}
          {transactions.length > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Showing {startEntry}–{endEntry} of {totalCount} transactions
              </p>

              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPageNo((p) => Math.max(1, p - 1))}
                    disabled={pageNo === 1}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === pageCount || Math.abs(p - pageNo) <= 1)
                    .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "…" ? (
                        <span key={`ellipsis-${idx}`} className="flex h-8 w-8 items-center justify-center text-xs text-gray-400">
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => setPageNo(item as number)}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                            pageNo === item
                              ? "bg-primary text-white"
                              : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          }`}
                        >
                          {item}
                        </button>
                      )
                    )}

                  <button
                    onClick={() => setPageNo((p) => Math.min(pageCount, p + 1))}
                    disabled={pageNo === pageCount}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <TransactionSummary
        isOpen={isSummaryOpen}
        onClose={closeSummaryModal}
        transaction={selectedTransaction}
        wallet={wallet}
      />
    </>
  );
};

export default TransactionsPage;